import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { Suspense, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ContentPreview from 'hb-react/shared/components/ContentPreview';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import Loader from 'hb-react/shared/components/Loader';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { useQueryParams } from 'hb-react/shared/hooks/useQueryParams';
import AmericanExpress from 'hb-react/shared/icons/Payments/AmericanExpress';
import Bancontact from 'hb-react/shared/icons/Payments/Bancontact';
import Ideal from 'hb-react/shared/icons/Payments/Ideal';
import Maestro from 'hb-react/shared/icons/Payments/Maestro';
import Mastercard from 'hb-react/shared/icons/Payments/Mastercard';
import Sepa from 'hb-react/shared/icons/Payments/Sepa';
import Visa from 'hb-react/shared/icons/Payments/Visa';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import { PaymentError } from './components/PaymentError';
import {
  STRIPE_PAYMENT_TYPES,
  STRIPE_REDIRECT_STATUS,
  STRIPE_BANK_REDIRECT_PAYMENT_METHODS,
} from './constants';
import BancontactForm from './containers/BancontactForm';
import IBANForm from './containers/IBANForm';
import IdealPayment from './containers/IdealPayment';
import LegacyPaymentForm from './containers/LegacyPaymentForm';
import PaymentForm from './containers/PaymentForm';
import StripeError from './errors/StripeError';
import { getPaymentMethodsConfiguration } from './utils';

import style from './index.module.scss';

const fetchPaymentAccountConfiguration = (
  paymentType,
  paymentTypeId,
  paymentCampaignId,
) =>
  axios.get(Routes.stripe_config_path(), {
    params: {
      payment_type: paymentType,
      payment_type_id: paymentTypeId,
      payment_campaign_id: paymentCampaignId,
    },
  });

const InjectedCheckoutForm = (props) => {
  const stripe = useStripe();
  const elements = useElements();

  const { description, image, selectedPaymentMethod, onPaymentCancel } = props;

  let paymentComponent;
  switch (selectedPaymentMethod) {
    case 'card':
      paymentComponent = (
        <PaymentForm {...props} stripe={stripe} elements={elements} />
      );
      break;
    case 'sepaDebit':
      paymentComponent = (
        <IBANForm {...props} stripe={stripe} elements={elements} />
      );
      break;
    case 'ideal':
      paymentComponent = (
        <IdealPayment {...props} stripe={stripe} elements={elements} />
      );
      break;
    case 'bancontact':
      paymentComponent = (
        <BancontactForm {...props} stripe={stripe} elements={elements} />
      );
      break;
    default:
      errorReporting.captureException(
        new StripeError('No selected payment method'),
      );
      displayNotif('error', s__('Stripe|Sorry, an error occurred'));
      onPaymentCancel();
  }

  return (
    <div data-testid="payment-provider-container">
      {description && <p>description</p>}
      {image && <img src={image} className={style.image} />}
      {paymentComponent}
    </div>
  );
};

const StripeInjector = (props) => {
  const [stripePromise, setStripePromise] = useState(null);
  const { selectedPaymentMethod, stripeAccountId } = props;
  const stripeOptions =
    selectedPaymentMethod === 'card' ? {} : { stripeAccount: stripeAccountId };

  useEffect(() => {
    import('@stripe/stripe-js').then(({ loadStripe }) =>
      setStripePromise(
        loadStripe(window.__HB_ENV__.STRIPE_PUBLISHABLE_KEY, stripeOptions),
      ),
    );
  }, []);

  return (
    stripePromise && (
      <Elements stripe={stripePromise}>
        <InjectedCheckoutForm {...props} />
      </Elements>
    )
  );
};

const PaymentMethodSelector = ({
  onPaymentCancel,
  selectedPaymentMethod,
  sepaDebitEnabled,
  setDisplayCloseModal,
  setFinalSelectedPaymentMethod,
  idealEnabled,
  bancontactEnabled,
}) => {
  const [choicePaymentMethod, setChoicePaymentMethod] = useState(
    STRIPE_PAYMENT_TYPES.CARD,
  );
  const [error, setError] = useState(false);
  const httpState = useQueryParams();

  useEffect(() => {
    if (
      Object.values(STRIPE_BANK_REDIRECT_PAYMENT_METHODS).includes(
        httpState.source_type,
      ) &&
      httpState.redirect_status === STRIPE_REDIRECT_STATUS.ERROR
    ) {
      setError(true);
      setChoicePaymentMethod(httpState.source_type);
    }
  }, [httpState]);

  const defaultInputs = [
    {
      value: STRIPE_PAYMENT_TYPES.CARD,
      title: s__('Stripe|Credit Card'),
      icons: {
        Visa,
        AmericanExpress,
        Maestro,
        Mastercard,
      },
      defaultChecked: true,
    },
  ];

  // To add an other payment type, just use this as an example
  if (sepaDebitEnabled) {
    defaultInputs.push({
      value: STRIPE_PAYMENT_TYPES.SEPA_DEBIT,
      title: s__('Stripe|SEPA Direct Debit'),
      icons: { Sepa },
    });
  }

  if (idealEnabled) {
    defaultInputs.push({
      value: STRIPE_PAYMENT_TYPES.IDEAL,
      title: s__('Stripe|iDEAL'),
      icons: { Ideal },
    });
  }

  if (bancontactEnabled) {
    defaultInputs.push({
      value: STRIPE_PAYMENT_TYPES.BANCONTACT,
      title: s__('Stripe|Bancontact'),
      icons: { Bancontact },
    });
  }

  return (
    <Box>
      {!selectedPaymentMethod && (
        <Box flexDirection="column" width="100%" mt={-4}>
          <Box>{s__('Stripe|Select a payment method')}</Box>
          {error && <PaymentError />}
          <Box flexDirection="column" mt={5}>
            {defaultInputs.map(
              ({ value, title, icons, defaultChecked = false }) => (
                <InputWrapper alignItems="center" key={value}>
                  <RadioInput
                    name="payment_type"
                    value={value}
                    id={value}
                    type="radio"
                    defaultChecked={defaultChecked}
                    checked={value === choicePaymentMethod}
                    className="uk-radio radio_buttons"
                    onChange={() => setChoicePaymentMethod(value)}
                  />
                  <Box
                    alignItems="center"
                    justifyContent="space-between"
                    ml={2}
                    width="100%"
                  >
                    <Label
                      css={css`
                        padding: 0;
                      `}
                      htmlFor={value}
                    >
                      {title}
                    </Label>
                    <Box>
                      {Object.keys(icons).map((key) => {
                        const IconComponent = icons[key];

                        return (
                          <IconComponent width={32} height={24} key={key} />
                        );
                      })}
                    </Box>
                  </Box>
                </InputWrapper>
              ),
            )}
          </Box>
          <Box justifyContent="flex-end" mt={5}>
            <Button
              mr={3}
              variant="secondary"
              onClick={() => {
                setDisplayCloseModal(true);
                onPaymentCancel();
              }}
            >
              {__('Cancel')}
            </Button>

            <Button
              onClick={() => {
                setFinalSelectedPaymentMethod(choicePaymentMethod);
              }}
            >
              {s__('Stripe|Proceed to payment')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const PaymentStripe = (props) => {
  const [loading, setLoading] = useState(false);
  const [paymentAccountConfig, setPaymentAccountConfig] = useState(null);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);
  const [selectedPaymentMethod, setFinalSelectedPaymentMethod] = useState(null);
  const {
    onPaymentCancel,
    payment_type: paymentType,
    payment_type_id: paymentTypeId,
    payment_campaign_id: paymentCampaignId,
    title,
  } = props;
  const { currentNetwork } = useDefaultAppContexts();

  useEffect(() => {
    setLoading(true);
    fetchPaymentAccountConfiguration(
      paymentType,
      paymentTypeId,
      paymentCampaignId,
    )
      .then(({ data }) => {
        setPaymentAccountConfig(data);
        setLoading(false);
      })
      .catch((error) => {
        errorReporting.captureException(
          new StripeError('Error getting payment account config', error),
        );
        displayNotif(
          'error',
          s__(
            'Stripe|Payment account configuration unavailable, please contact a network administrator',
          ),
        );
        onPaymentCancel();
      });
  }, []);

  const {
    sepaDebitEnabled,
    idealEnabled,
    bancontactEnabled,
    onlyCreditCardEnabled,
  } = getPaymentMethodsConfiguration({
    paymentAccountConfig,
  });

  const closeButtonText = s__('Accessibility|Close payment window');

  if (!currentNetwork?.network_configuration?.stripe_sca) {
    return <LegacyPaymentForm {...props} />;
  }

  if (onlyCreditCardEnabled) {
    return (
      <ContentPreview
        portalProps={{
          defaultOpen: true,
          closeOnOutsideClick: false,
          closeOnEsc: true,
          onClose: onPaymentCancel,
        }}
        title={title}
        size="small"
        screenReadersCloseButtonText={closeButtonText}
        modalPreviewContainerClassNames="modal-preview__container--bg-white"
      >
        <div>
          {loading && <Loader fillParent />}
          {paymentAccountConfig && (
            <StripeInjector
              paymentAccountConfig={paymentAccountConfig}
              selectedPaymentMethod="card"
              {...props}
            />
          )}
        </div>
      </ContentPreview>
    );
  }

  if (selectedPaymentMethod) {
    return (
      <ContentPreview
        portalProps={{
          defaultOpen: true,
          closeOnOutsideClick: false,
          closeOnEsc: true,
          onClose: onPaymentCancel,
        }}
        title={title}
        size="small"
        screenReadersCloseButtonText={closeButtonText}
        modalPreviewContainerClassNames="modal-preview__container--bg-white"
      >
        <div>
          {loading && <Loader fillParent />}
          {paymentAccountConfig && (
            <Box flexDirection="column" width="100%">
              <StripeInjector
                paymentAccountConfig={paymentAccountConfig}
                selectedPaymentMethod={selectedPaymentMethod}
                {...props}
              />
            </Box>
          )}
        </div>
      </ContentPreview>
    );
  }

  return (
    !displayCloseModal && (
      <ModalWithTemplate
        onCloseModal={() => {
          setDisplayCloseModal(true);
          onPaymentCancel();
        }}
        header={title}
        width="sm"
        screenReadersCloseButtonText={closeButtonText}
      >
        <div style={{ width: '100%' }} data-testid="template-payment-modal">
          {paymentAccountConfig && (
            <PaymentMethodSelector
              setDisplayCloseModal={setDisplayCloseModal}
              paymentAccountConfig={paymentAccountConfig}
              onPaymentCancel={onPaymentCancel}
              sepaDebitEnabled={sepaDebitEnabled}
              idealEnabled={idealEnabled}
              bancontactEnabled={bancontactEnabled}
              setFinalSelectedPaymentMethod={setFinalSelectedPaymentMethod}
              selectedPaymentMethod={selectedPaymentMethod}
              {...props}
            />
          )}
        </div>
      </ModalWithTemplate>
    )
  );
};

const PaymentStripeWrapper = (props) => (
  <Suspense fallback={null}>
    <ThemeProvider theme="fo">
      <PaymentStripe {...props} />
    </ThemeProvider>
  </Suspense>
);

PaymentStripe.propTypes = {
  stripeAccountId: PropTypes.string,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  endpointUrl: PropTypes.string.isRequired,
  image: PropTypes.string,
  endpointData: PropTypes.object,
  onPaymentSuccess: PropTypes.func,
  onPaymentError: PropTypes.func,
  onPaymentCancel: PropTypes.func,
};

export default PaymentStripeWrapper;

const InputWrapper = styled(Box).attrs(() => ({
  py: 3,
}))`
  border-width: 2px;

  &:first-child {
    border: 1px solid ${({ theme }) => theme.colors.horizon};
    border-width: 1px 0px;
  }
  &:not(:first-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.horizon};
  }
`;

const Label = styled.label`
  color: ${({ theme }) => theme.input.color};
  cursor: ${({ htmlFor }) => (htmlFor ? 'pointer' : 'default')} !important;
  font-size: ${({ fontSize, theme }) => fontSize || theme.fontSizes[2]};
  font-weight: ${({ theme }) => theme.fontWeights[1]};
  position: relative;
  user-select: none;
  ${({ disabled }) => disabled && 'pointer-events: none;'}
`;

const RadioInput = styled.input`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 10px;
  cursor: pointer;
  display: inline-block;
  height: 18px;
  margin: 0 !important;
  padding: 0 !important;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  ${({ disabled }) => disabled && 'pointer-events: none;'}
  position: relative;
  width: 19px;
  -webkit-appearance: none;

  &:after {
    background-color: transparent;
    border-radius: 25px;
    content: '';
    display: block;
    height: 10px;
    left: 3px;
    position: relative;
    top: 3px;
    width: 10px;
  }

  &:checked:after {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;
